import { ExternalIcon } from "@icons/ExternalIcon";
import { MapControl } from "./MapControl";
import { useCallback, useState, useRef, useEffect } from "react";
import { LayerSelector } from "./LayerSelector";
import "./LayerControl.css";
import { useMap } from "@vis.gl/react-google-maps";

const LayerDesktopSelector = ({
  anchorRef,
  isSelectorOpen,
  onClose,
}: {
  anchorRef: React.RefObject<HTMLDivElement>;
  isSelectorOpen: boolean;
  onClose: () => void;
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const map = useMap();

  useEffect(() => {
    if (anchorRef.current && overlayRef.current) {
      const parentRect = anchorRef.current.getBoundingClientRect();
      const buttonRect = anchorRef.current.getBoundingClientRect();
      const overlayRect = overlayRef.current.getBoundingClientRect();
      setPosition({
        top: buttonRect.top - parentRect.top,
        left: buttonRect.left - parentRect.left - overlayRect.width - 12,
      });
    }
  }, [anchorRef, isSelectorOpen]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        overlayRef.current &&
        !overlayRef.current.contains(event.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);

    const listener = map?.addListener("click", () => {
      onClose();
    });

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      listener?.remove();
    };
  }, [onClose, anchorRef, map]);

  return (
    <div
      ref={overlayRef}
      className="layer-control-overlay"
      style={{
        top: position?.top ?? -500,
        left: position?.left ?? -500,
        position: "absolute",
      }}>
      <LayerSelector />
    </div>
  );
};

const LayerControl = () => {
  const [isSelectorOpen, setIsSelectorOpen] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(
    null,
  ) as React.RefObject<HTMLDivElement>;

  const handlePress = useCallback(() => {
    setIsSelectorOpen(!isSelectorOpen);
  }, [isSelectorOpen]);

  const handleClose = useCallback(() => {
    setIsSelectorOpen(false);
  }, []);

  return (
    <>
      <MapControl
        items={[
          {
            icon: <ExternalIcon svgName="map" />,
            key: "layers",
            onPress: handlePress,
          },
        ]}
        buttonRef={buttonRef}
      />
      {isSelectorOpen && (
        <LayerDesktopSelector
          anchorRef={buttonRef}
          isSelectorOpen={isSelectorOpen}
          onClose={handleClose}
        />
      )}
    </>
  );
};

export { LayerControl };
